import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { useGeckoPermissions } from 'features/permissions'
import { useCallback, useMemo, useState } from 'react'
import {
  ApprovalUserRef,
  PERMISSION_CANTILEVER_ACCESS_ADMIN,
  ReportApprovalWorkflow,
  SignoffState,
} from 'types'

import {
  ApprovalWorkflowDraft,
  buildApprovalWorkflow,
  canManageWorkflow,
  deriveSignoffFromWorkflow,
  SIGNOFF_ROLE_ORDER,
} from './approvalRules'

interface UseApprovalWorkflowParams {
  metadataEntryId: string
  workflow: ReportApprovalWorkflow | undefined
  signoff: SignoffState | undefined
  onSaved: (next: {
    approvalWorkflow: ReportApprovalWorkflow
    signoff: SignoffState
  }) => void
}

/** Persists who must sign a report, mirroring assignees onto `signoff` for the PDF cover. */
export function useApprovalWorkflow({
  metadataEntryId,
  workflow,
  signoff,
  onSaved,
}: UseApprovalWorkflowParams) {
  const { rolodexPatch } = useRolodexEdit()
  const { user, hasPermissions } = useGeckoPermissions()
  const [isSaving, setIsSaving] = useState(false)

  const currentUser = useMemo<ApprovalUserRef | undefined>(
    () => user && { userId: user.id, name: user.name, email: user.email },
    [user]
  )
  const canManage = canManageWorkflow(
    workflow,
    user?.id,
    hasPermissions([PERMISSION_CANTILEVER_ACCESS_ADMIN])
  )

  const saveWorkflow = useCallback(
    async (draft: ApprovalWorkflowDraft) => {
      if (!currentUser) throw new Error('Your account has not finished loading')
      const now = new Date().toISOString()
      const approvalWorkflow = buildApprovalWorkflow(
        draft,
        workflow,
        currentUser,
        now
      )
      const nextSignoff = deriveSignoffFromWorkflow(
        approvalWorkflow,
        signoff,
        now
      )

      setIsSaving(true)
      try {
        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              approval_workflow: {
                ...approvalWorkflow,
                // Rolodex deep-merges, so dropped roles must be nulled explicitly
                assignments: Object.fromEntries(
                  SIGNOFF_ROLE_ORDER.map((role) => [
                    role,
                    approvalWorkflow.assignments[role] ?? null,
                  ])
                ),
              },
              signoff: nextSignoff,
            },
          },
        ])
        onSaved({ approvalWorkflow, signoff: nextSignoff })
        return approvalWorkflow
      } finally {
        setIsSaving(false)
      }
    },
    [currentUser, metadataEntryId, onSaved, rolodexPatch, signoff, workflow]
  )

  return { currentUser, canManage, isSaving, saveWorkflow }
}
